import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { AppShell } from './components/layout/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';

// Role-aware default home: redirects strict-view roles AWAY from /dashboard.
// May 26, 2026 — Lead Triage Coordinator (Deepika) lands on /leads/triage.
function RoleAwareHome() {
  const { user } = useAuthStore.getState();
  if (user?.role?.name === 'lead_triage_coordinator') {
    return <Navigate to="/leads/triage" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

// Role-aware dashboard guard: same redirect even if a strict-view user types
// /dashboard directly into the URL.
function RoleAwareDashboard() {
  const { user } = useAuthStore.getState();
  if (user?.role?.name === 'lead_triage_coordinator') {
    return <Navigate to="/leads/triage" replace />;
  }
  return <DashboardPage />;
}
// CRM
import { ContactsPage } from './pages/crm/ContactsPage';
import { ContactDetailPage } from './pages/crm/ContactDetailPage';
import { AccountsPage } from './pages/crm/AccountsPage';
import { DealsPage } from './pages/crm/DealsPage';
import { DealDetailPage } from './pages/crm/DealDetailPage';
// Sales
import { ProposalsPage } from './pages/sales/ProposalsPage';
import { ProposalEditorPage } from './pages/sales/ProposalEditorPage';
import { DiscountRequestsPage } from './pages/sales/DiscountRequestsPage';
import { EstimatesPage } from './pages/sales/EstimatesPage';
import { EstimateEditorPage } from './pages/sales/EstimateEditorPage';
import { SalesOrdersPage } from './pages/sales/SalesOrdersPage';
import { SalesOrderEditorPage } from './pages/sales/SalesOrderEditorPage';
import { SalesOrderDetailPage } from './pages/sales/SalesOrderDetailPage';
import { SalesOrderFulfilmentPage } from './pages/sales/SalesOrderFulfilmentPage';
import { AccountDetailPage } from './pages/crm/AccountDetailPage';
// Sales — generic (renamed from VedaAgro-prefixed Apr 22 2026, Phase P2)
import { FinancialApprovalPage } from './pages/sales/FinancialApprovalPage';
import { FinancialApprovalsListPage } from './pages/sales/FinancialApprovalsListPage';
import { SecurityPaymentPage } from './pages/sales/SecurityPaymentPage';
import { SamplesPage } from './pages/sales/SamplesPage';
import { LandedCostPage } from './pages/sales/LandedCostPage';
import { DealClosurePage } from './pages/sales/DealClosurePage';
// Finance
import { InvoicesPage } from './pages/finance/InvoicesPage';
import { InvoiceEditorPage } from './pages/finance/InvoiceEditorPage';
import { ExpensesPage } from './pages/finance/ExpensesPage';
import { PaymentsPage } from './pages/finance/PaymentsPage';
import { CreditNotesPage } from './pages/finance/CreditNotesPage';
import { DebitNotesPage } from './pages/finance/DebitNotesPage';
import { InvoiceSwapPage } from './pages/finance/InvoiceSwapPage';
// Projects
import { ProjectsPage } from './pages/projects/ProjectsPage';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage';
import { TasksPage } from './pages/projects/TasksPage';
// Procurement
import { VendorsPage } from './pages/procurement/VendorsPage';
import { PurchaseOrdersPage } from './pages/procurement/PurchaseOrdersPage';
import { PurchaseOrderEditorPage } from './pages/procurement/PurchaseOrderEditorPage';
// Integrations — generic (renamed Apr 22 2026)
import { TallySyncPage } from './pages/integrations/TallySyncPage';
// Service
import { ServiceTicketsPage } from './pages/service/ServiceTicketsPage';
import { ServiceTicketDetailPage } from './pages/service/ServiceTicketDetailPage';
import { ComplaintIntakePage } from './pages/service/ComplaintIntakePage';
import { WarrantyVerificationPage } from './pages/service/WarrantyVerificationPage';
import { PreVisitChecklistPage } from './pages/service/PreVisitChecklistPage';
import { ProductDiagnosisPage } from './pages/service/ProductDiagnosisPage';
import { OnSiteExecutionPage } from './pages/service/OnSiteExecutionPage';
import { WarrantyClaimPage } from './pages/service/WarrantyClaimPage';
import { WarrantiesPage } from './pages/service/WarrantiesPage';
// Service & Warranty module (NEW — Apr 2026)
import { ServiceTicketCreatePage } from './pages/service/ServiceTicketCreatePage';
import { ComponentIssuePage } from './pages/service/ComponentIssuePage';
import { ServicePhotosPage } from './pages/service/ServicePhotosPage';
import { ServiceAuditPage } from './pages/service/ServiceAuditPage';
import { OtpVerifyPage } from './pages/service/OtpVerifyPage';
import { ProductUnitWarrantyHistoryPage } from './pages/service/ProductUnitWarrantyHistoryPage';
// Support (standalone flat FSM)
import { SupportTicketsPage } from './pages/support/SupportTicketsPage';
import { SupportTicketDetailPage } from './pages/support/SupportTicketDetailPage';
// Product docs admin
import { ProductDocsAdminPage } from './pages/products/ProductDocsAdminPage';
// HR
import { EmployeesPage } from './pages/hr/EmployeesPage';
import { LeavePage } from './pages/hr/LeavePage';
import { IncentivesPage } from './pages/hr/IncentivesPage';
// Automation
import { WorkflowsPage } from './pages/automation/WorkflowsPage';
// AI
import { AIAssistantPage } from './pages/ai/AIAssistantPage';
import { AIAgentsPage } from './pages/ai/AIAgentsPage';
import { APIIntegrationsPage } from './pages/ai/APIIntegrationsPage';
import CreditsPage from './pages/ai/CreditsPage';
import { ProposalBuilderPage } from './pages/sales/ProposalBuilderPage';
import { KnowledgeBasePage } from './pages/knowledge-base/KnowledgeBasePage';
// Telecaller
import { CallLogsPage } from './pages/telecaller/CallLogsPage';
import { ProposalTrackingPage } from './pages/telecaller/ProposalTrackingPage';
import { TeamPerformancePage } from './pages/telecaller/TeamPerformancePage';
import { TelecallerTodayPage } from './pages/telecaller/TelecallerTodayPage';
import { CallWorkspacePage } from './pages/telecaller/CallWorkspacePage';
// ChatKart Call Handoff — agent call history (M4, Apr 26 2026)
const MyCallsPage = lazy(() =>
  import('./pages/calls/MyCallsPage').then((m) => ({ default: m.MyCallsPage })),
);
// Profile
import { ProfilePage } from './pages/profile/ProfilePage';
// Settings
import { SettingsPage } from './pages/settings/SettingsPage';
import { BillingPlansPage } from './pages/settings/BillingPlansPage';
import { AIAssistantSettingsPage } from './pages/settings/AIAssistantSettingsPage';
// Super Admin
import { SuperAdminDashboard } from './pages/admin/SuperAdminDashboard';
import { TenantManagementPage } from './pages/admin/TenantManagementPage';
import { BillingPage } from './pages/admin/BillingPage';
import { SystemHealthPage } from './pages/admin/SystemHealthPage';
import { TeamPage } from './pages/admin/TeamPage';
import { AuditLogsPage } from './pages/admin/AuditLogsPage';
import { ResourceManagement } from './pages/admin/ResourceManagement';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';
import { CarrierNumbersPage } from './pages/admin/CarrierNumbersPage';
import { InstallationsFocusPage } from './pages/admin/InstallationsFocusPage';
// Sun Stellar Pool Heat Pump Calculator (Track D, May 2026 — Ananya).
// Module-gated by `pool_heat_pump_calculator`; backend 404s the routes when
// the tenant flag isn't set, so listing them here is harmless for non–Sun-Stellar
// tenants. UI gate is on the sidebar entry (which uses tenant.enabledModules).
import { MeetingModeLauncher } from './pages/sales/MeetingMode/MeetingModeLauncher';
import { PoolCalculatorPage } from './pages/sales/MeetingMode/PoolCalculatorPage';
import { CalculatorChooserPage } from './pages/sales/MeetingMode/CalculatorChooserPage';
import { CommercialHeatPumpCalculatorPage } from './pages/sales/MeetingMode/CommercialHeatPumpCalculatorPage';
import { SsTankCalculatorPage } from './pages/sales/MeetingMode/SsTankCalculatorPage';
import CustomizedTankCalculatorPage from './pages/sales/MeetingMode/CustomizedTankCalculatorPage';
import { DomesticHeatPumpCalculatorPage } from './pages/sales/MeetingMode/DomesticHeatPumpCalculatorPage';
import { PoolModelsAdmin } from './pages/admin/PoolModelsAdmin';
import { CommercialHeatPumpModelsAdmin } from './pages/admin/CommercialHeatPumpModelsAdmin';
import { SsTankModelsAdmin } from './pages/admin/SsTankModelsAdmin';
import { DomesticHeatPumpModelsAdmin } from './pages/admin/DomesticHeatPumpModelsAdmin';
// Procurement Automation
import { QuotationJobsPage } from './pages/procurement/QuotationJobsPage';
import { QuotationJobDetailPage } from './pages/procurement/QuotationJobDetailPage';
// Procurement contracts
import { PurchaseContractsPage } from './pages/procurement/PurchaseContractsPage';
import { TransportContractsPage } from './pages/procurement/TransportContractsPage';
// Purchase Dashboard & Vendor Rate Comparison
import { PurchaseDashboardPage } from './pages/procurement/PurchaseDashboardPage';
import { VendorRateComparisonPage } from './pages/procurement/VendorRateComparisonPage';
// Service escalation
import { EscalationRulesPage } from './pages/service/EscalationRulesPage';
// Service SOP
import { ServiceSOPDashboardPage } from './pages/service/ServiceSOPDashboardPage';
import { ComplaintDetailPage } from './pages/service/ComplaintDetailPage';
// ITBP SWH Installation Flow (Apr 2026)
import { ITBPInstallationsPage } from './pages/service/ITBPInstallationsPage';
import { InstallationJobDetailPage } from './pages/service/InstallationJobDetailPage';
import { ITBPReportsPage } from './pages/service/ITBPReportsPage';
// Lead Integrations
import { LeadsPage } from './pages/crm/LeadsPage';
// Apr 26, 2026 (Squad-B) — Lead Manager workflow stubs
import { UnverifiedLeadsPage } from './pages/leads/UnverifiedLeadsPage';
import { VerifiedLeadsPage } from './pages/leads/VerifiedLeadsPage';
import { LeadApprovalsPage } from './pages/leads/LeadApprovalsPage';
// Phase 4 (2026-05-26) — Deepika's lead-triage dashboard
import LeadTriageDashboard from './pages/leads/LeadTriageDashboard';
import { AdminLeadApprovalsPage } from './pages/admin/AdminLeadApprovalsPage';
// Customers
import { CustomersPage } from './pages/crm/CustomersPage';
// SO-PO Orchestration
import { SoPoMappingPage } from './pages/sales/SoPoMappingPage';
import { MarginCalculatorPage } from './pages/sales/MarginCalculatorPage';
// Field Partners
import { FieldPartnerDirectoryPage } from './pages/field-partners/FieldPartnerDirectoryPage';
import { KycReviewPage } from './pages/field-partners/KycReviewPage';
import { InstallationClaimsPage } from './pages/field-partners/InstallationClaimsPage';
import { IncentiveRulesPage } from './pages/field-partners/IncentiveRulesPage';
import { PayoutsConsolePage } from './pages/field-partners/PayoutsConsolePage';
import { FraudReviewPage } from './pages/field-partners/FraudReviewPage';
import { FieldProgramConfigPage } from './pages/field-partners/FieldProgramConfigPage';
// Inventory Expansion (Apr 2026)
import { VendorReturnsPage } from './pages/procurement/VendorReturnsPage';
import { VendorReturnDetailPage } from './pages/procurement/VendorReturnDetailPage';
import { PurchaseRequestsPage } from './pages/procurement/PurchaseRequestsPage';
import { PurchaseRequestDetailPage } from './pages/procurement/PurchaseRequestDetailPage';
// Portals (standalone, no AppShell)
import { ClientPortalPage } from './pages/portals/ClientPortalPage';
import { VendorPortalPage } from './pages/portals/VendorPortalPage';
import { BorrowerPortalPage } from './pages/portals/BorrowerPortalPage';
import { PaymentConfirmationForm } from './pages/portals/PaymentConfirmationForm';
// Sales Manager dual-tab pages (Apr 22, 2026 exec sprint — replaces /tasks + /team-performance for sales role)
import { ActivitiesDualPage } from './pages/sales/ActivitiesDualPage';
import { RepsDualPage } from './pages/sales/RepsDualPage';
// 404 fallback (Apr 23, 2026 — Agent 4 fix for silent blank routes)
import { NotFound } from './pages/NotFound';
// Lead Manager (Apr 28, 2026 — Squad-D, 12 screens). The 12th screen
// (admin approvals) reuses the existing AdminLeadApprovalsPage import that
// Squad-B already wired further up — Squad-D just upgraded the body of that
// component with the richer card-based queue from the design canvas.
import { LeadManagerHome } from './pages/lead-manager/LeadManagerHome';
import { UnverifiedQueuePage as LMUnverifiedQueuePage } from './pages/lead-manager/UnverifiedQueuePage';
import { VerifyLeadPage } from './pages/lead-manager/VerifyLeadPage';
import { VerifiedPoolPage as LMVerifiedPoolPage } from './pages/lead-manager/VerifiedPoolPage';
import { AssignedOversightPage } from './pages/lead-manager/AssignedOversightPage';
import { ReassignmentOutboxPage } from './pages/lead-manager/ReassignmentOutboxPage';
import { LeadMetricsPage } from './pages/lead-manager/LeadMetricsPage';
import { LeadSearchPage } from './pages/lead-manager/LeadSearchPage';
import { LeadDetailPage } from './pages/lead-manager/LeadDetailPage';
import { LeadManagerSettingsPage } from './pages/lead-manager/LeadManagerSettingsPage';

// ─── Lazy-loaded spinner ───
function LazySpinner() {
  return (
    <div className="flex h-full items-center justify-center py-32">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
    </div>
  );
}

// ─── Lazy: Accounting (heavy — Tally-grade ledger with 12 tabs) ───
const AccountingPage = lazy(() => import('./pages/accounting/AccountingPage').then(m => ({ default: m.AccountingPage })));
const LedgerAccountingPage = lazy(() => import('./pages/accounting/LedgerAccountingPage').then(m => ({ default: m.LedgerAccountingPage })));
const PaymentConfirmationPage = lazy(() => import('./pages/accounting/PaymentConfirmationPage').then(m => ({ default: m.PaymentConfirmationPage })));

/* ── Sprint 2-3 — Accounting spine (May 24 2026) ───────────────────────
 * Agent KK — Purchase Invoices (vendor bills + editor)
 * Agent LL — Ledger pack (5 reports + voucher register)
 * Agent MM — Coupon editor
 * Agent JJ — PaymentSchedulePanel mounted inline in InvoiceEditorPage.
 */
const PurchaseInvoicesPage = lazy(() => import('./pages/PurchaseInvoices/PurchaseInvoicesPage').then(m => ({ default: m.PurchaseInvoicesPage })));
const PurchaseInvoiceEditor = lazy(() => import('./pages/PurchaseInvoices/PurchaseInvoiceEditor').then(m => ({ default: m.PurchaseInvoiceEditor })));
const VoucherRegister = lazy(() => import('./pages/Ledger/VoucherRegister'));
const AccountLedger = lazy(() => import('./pages/Ledger/AccountLedger'));
const TrialBalance = lazy(() => import('./pages/Ledger/TrialBalance'));
const ProfitLoss = lazy(() => import('./pages/Ledger/ProfitLoss'));
const BalanceSheet = lazy(() => import('./pages/Ledger/BalanceSheet'));
const CouponEditor = lazy(() => import('./pages/Coupons/CouponEditor').then(m => ({ default: m.CouponEditor })));

// ─── Lazy: Analytics (Recharts-heavy) ───
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));

// ─── Lazy: Lending Ops (Track B, blinkemi launch — Jul 2026) ───
// Module-gated by `lending` on the SIDEBAR entries (tenant.enabledModules),
// same pattern as pool_heat_pump_calculator above: the backend authenticates +
// tenant-scopes every /api/v1/lending/* call, so registering the routes here
// is harmless for tenants without the module.
const LendingDashboardPage = lazy(() => import('./pages/lending/LendingDashboardPage').then(m => ({ default: m.LendingDashboardPage })));
const LoanApplicationsPage = lazy(() => import('./pages/lending/LoanApplicationsPage').then(m => ({ default: m.LoanApplicationsPage })));
const LendingLoansPage = lazy(() => import('./pages/lending/LoansPage').then(m => ({ default: m.LoansPage })));
const LendingLoanDetailPage = lazy(() => import('./pages/lending/LoanDetailPage').then(m => ({ default: m.LoanDetailPage })));
const LendingDisbursementsPage = lazy(() => import('./pages/lending/DisbursementsPage').then(m => ({ default: m.DisbursementsPage })));
const LendingRepaymentsPage = lazy(() => import('./pages/lending/RepaymentsPage').then(m => ({ default: m.RepaymentsPage })));
const LendingCollectionsPage = lazy(() => import('./pages/lending/CollectionsPage').then(m => ({ default: m.CollectionsPage })));
const LendingDecisionRulesPage = lazy(() => import('./pages/lending/DecisionRulesPage').then(m => ({ default: m.DecisionRulesPage })));

// ─── Lazy: Nishtha Grievance Desk (Track F, Sep 21 2026) ───
// Staff screens gated by module 'lending' on the sidebar entries (same
// pattern as Lending Ops above); partner screens fenced server-side
// (partner-scope.ts, Track B) and hidden from any non-partner sidebar.
const GrievanceDeskPage = lazy(() => import('./pages/grievances/GrievanceDeskPage').then(m => ({ default: m.GrievanceDeskPage })));
const GrievanceCasePage = lazy(() => import('./pages/grievances/GrievanceCasePage').then(m => ({ default: m.GrievanceCasePage })));
const GrievancePartnersPage = lazy(() => import('./pages/grievances/GrievancePartnersPage').then(m => ({ default: m.GrievancePartnersPage })));
const GrievanceSettingsPage = lazy(() => import('./pages/grievances/GrievanceSettingsPage').then(m => ({ default: m.GrievanceSettingsPage })));
const GrievanceHowItWorksPage = lazy(() => import('./pages/grievances/GrievanceHowItWorksPage').then(m => ({ default: m.GrievanceHowItWorksPage })));
const GrievanceApprovalsPage = lazy(() => import('./pages/grievances/GrievanceApprovalsPage').then(m => ({ default: m.GrievanceApprovalsPage })));
const PartnerCasesPage = lazy(() => import('./pages/grievances/PartnerCasesPage').then(m => ({ default: m.PartnerCasesPage })));
const PartnerCasePage = lazy(() => import('./pages/grievances/PartnerCasePage').then(m => ({ default: m.PartnerCasePage })));

// ─── Lazy: Nishtha Call Audit (Oct 12 2026) ───
const CallAuditOverviewPage = lazy(() => import('./pages/call-audit/CallAuditOverviewPage').then(m => ({ default: m.CallAuditOverviewPage })));
const CallAuditCallsPage = lazy(() => import('./pages/call-audit/CallAuditPages').then(m => ({ default: m.CallAuditCallsPage })));
const CallAuditOtherAppsPage = lazy(() => import('./pages/call-audit/CallAuditPages').then(m => ({ default: m.CallAuditOtherAppsPage })));
const CallAuditRepsPage = lazy(() => import('./pages/call-audit/CallAuditPages').then(m => ({ default: m.CallAuditRepsPage })));
const CallAuditRepPage = lazy(() => import('./pages/call-audit/CallAuditPages').then(m => ({ default: m.CallAuditRepPage })));
const CallAuditReviewPage = lazy(() => import('./pages/call-audit/CallAuditPages').then(m => ({ default: m.CallAuditReviewPage })));
const PartnerCallAuditPage = lazy(() => import('./pages/call-audit/CallAuditPages').then(m => ({ default: m.PartnerCallAuditPage })));

// ─── Lazy: Nishtha Developer Access + Bank gateway (Oct 8 2026) ───
const DeveloperAccessPage = lazy(() => import('./pages/dev-access/DeveloperAccessPage').then(m => ({ default: m.DeveloperAccessPage })));
const ShareReviewPage = lazy(() => import('./pages/dev-access/ShareReviewPage').then(m => ({ default: m.ShareReviewPage })));
const ShareDetailPage = lazy(() => import('./pages/dev-access/ShareDetailPage').then(m => ({ default: m.ShareDetailPage })));
const HowSharingWorksPage = lazy(() => import('./pages/dev-access/HowSharingWorksPage').then(m => ({ default: m.HowSharingWorksPage })));
const BankGatewayPage = lazy(() => import('./pages/dev-access/BankGatewayPage').then(m => ({ default: m.BankGatewayPage })));
// LSP developer (fenced lsp_developer login)
const MyCredentialsPage = lazy(() => import('./pages/developer/MyCredentialsPage').then(m => ({ default: m.MyCredentialsPage })));
const AskForAccessPage = lazy(() => import('./pages/developer/AskForAccessPage').then(m => ({ default: m.AskForAccessPage })));
const OpenOncePage = lazy(() => import('./pages/developer/OpenOncePage').then(m => ({ default: m.OpenOncePage })));
// Oct 13, 2026 — How-to videos (static /tour-videos files; staff L01–L11, developer L12–L16)
const HowToVideosPage = lazy(() => import('./pages/dev-access/HowToVideosPage').then(m => ({ default: m.HowToVideosPage })));
const DeveloperVideosPage = lazy(() => import('./pages/developer/DeveloperVideosPage').then(m => ({ default: m.DeveloperVideosPage })));

// ─── Lazy: Manufacturing (20 pages) ───
const BOMEditorPage = lazy(() => import('./pages/manufacturing/BOMEditorPage').then(m => ({ default: m.BOMEditorPage })));
const BOMsPage = lazy(() => import('./pages/manufacturing/BOMsPage').then(m => ({ default: m.BOMsPage })));
const ProductionOrderCreatePage = lazy(() => import('./pages/manufacturing/ProductionOrderCreatePage').then(m => ({ default: m.ProductionOrderCreatePage })));
const ProductionOrderDetailPage = lazy(() => import('./pages/manufacturing/ProductionOrderDetailPage').then(m => ({ default: m.ProductionOrderDetailPage })));
const QCTemplatesPage = lazy(() => import('./pages/manufacturing/QCTemplatesPage').then(m => ({ default: m.QCTemplatesPage })));
const RejectionsPage = lazy(() => import('./pages/manufacturing/RejectionsPage').then(m => ({ default: m.RejectionsPage })));
// Apr 24, 2026 — v3 Production Dashboard is the canonical Production view.
// The legacy standalone ManufacturingDashboardPage (at /manufacturing/shop-floor)
// has been removed; its full kanban is now embedded at the bottom of the v3
// dashboard via <PipelineKanbanBoard />.
const ProductionDashboardV3 = lazy(() => import('./pages/dashboard/roles/ProductionDashboard').then(m => ({ default: m.ProductionDashboard })));
const ProductionPlanningPage = lazy(() => import('./pages/manufacturing/ProductionPlanningPage').then(m => ({ default: m.ProductionPlanningPage })));
const BucketedProductionPlanPage = lazy(() => import('./pages/manufacturing/BucketedProductionPlanPage').then(m => ({ default: m.BucketedProductionPlanPage })));
const ShiftControlPage = lazy(() => import('./pages/manufacturing/ShiftControlPage').then(m => ({ default: m.ShiftControlPage })));
const RMInspectionPage = lazy(() => import('./pages/manufacturing/RMInspectionPage').then(m => ({ default: m.RMInspectionPage })));
const ProcessKanbanPage = lazy(() => import('./pages/manufacturing/ProcessKanbanPage').then(m => ({ default: m.ProcessKanbanPage })));
const QCManagementPage = lazy(() => import('./pages/manufacturing/QCManagementPage').then(m => ({ default: m.QCManagementPage })));
const ManufacturingDispatchPage = lazy(() => import('./pages/manufacturing/ManufacturingDispatchPage').then(m => ({ default: m.ManufacturingDispatchPage })));
const ManufacturingAlertsPage = lazy(() => import('./pages/manufacturing/ManufacturingAlertsPage'));
const ProductionPipelinePage = lazy(() => import('./pages/manufacturing/ProductionPipelinePage').then(m => ({ default: m.ProductionPipelinePage })));
const EndOfShiftReportPage = lazy(() => import('./pages/manufacturing/EndOfShiftReportPage').then(m => ({ default: m.EndOfShiftReportPage })));
const BarcodeManagementPage = lazy(() => import('./pages/manufacturing/BarcodeManagementPage').then(m => ({ default: m.BarcodeManagementPage })));
const ScrapManagementPage = lazy(() => import('./pages/manufacturing/ScrapManagementPage').then(m => ({ default: m.ScrapManagementPage })));
// PipelineConfigPage merged into ProcessKanbanPage as a drawer — route redirects to process-kanban
const FGActivationPage = lazy(() => import('./pages/manufacturing/FGActivationPage').then(m => ({ default: m.FGActivationPage })));
const FloorInspectionPage = lazy(() => import('./pages/manufacturing/FloorInspectionPage').then(m => ({ default: m.FloorInspectionPage })));
const SsTankPackingPage = lazy(() => import('./pages/manufacturing/packing/SsTankPackingPage').then(m => ({ default: m.SsTankPackingPage })));
const SolarWhPackingPage = lazy(() => import('./pages/manufacturing/packing/SolarWhPackingPage').then(m => ({ default: m.SolarWhPackingPage })));
const HeatPumpPackingPage = lazy(() => import('./pages/manufacturing/packing/HeatPumpPackingPage').then(m => ({ default: m.HeatPumpPackingPage })));

// ─── Lazy: Warehouse (20 pages) ───
const InventoryDashboardPage = lazy(() => import('./pages/warehouse/InventoryDashboardPage').then(m => ({ default: m.InventoryDashboardPage })));
const InwardDashboardPage = lazy(() => import('./pages/warehouse/InwardDashboardPage').then(m => ({ default: m.InwardDashboardPage })));
const DispatchDashboardPage = lazy(() => import('./pages/warehouse/DispatchDashboardPage').then(m => ({ default: m.DispatchDashboardPage })));
const ProductsPage = lazy(() => import('./pages/warehouse/ProductsPage').then(m => ({ default: m.ProductsPage })));
const ProductCataloguePage = lazy(() => import('./pages/warehouse/ProductCataloguePage').then(m => ({ default: m.ProductCataloguePage })));
const ProductSettingsPage = lazy(() => import('./pages/warehouse/ProductSettingsPage').then(m => ({ default: m.ProductSettingsPage })));
const InventoryPage = lazy(() => import('./pages/warehouse/InventoryPage').then(m => ({ default: m.InventoryPage })));
const InventoryPipelinePage = lazy(() => import('./pages/warehouse/InventoryPipelinePage').then(m => ({ default: m.InventoryPipelinePage })));
const ProductionPage = lazy(() => import('./pages/warehouse/ProductionPage').then(m => ({ default: m.ProductionPage })));
const StockAuditPage = lazy(() => import('./pages/warehouse/StockAuditPage').then(m => ({ default: m.StockAuditPage })));
const BatchBarcodePrintPage = lazy(() => import('./pages/warehouse/BatchBarcodePrintPage').then(m => ({ default: m.BatchBarcodePrintPage })));
const WarehouseLocationsPage = lazy(() => import('./pages/warehouse/WarehouseLocationsPage').then(m => ({ default: m.WarehouseLocationsPage })));
const MaterialBoxesPage = lazy(() => import('./pages/warehouse/MaterialBoxesPage').then(m => ({ default: m.MaterialBoxesPage })));
const ProductUnitsPage = lazy(() => import('./pages/warehouse/ProductUnitsPage'));
const BarcodeLifecyclePage = lazy(() => import('./pages/warehouse/BarcodeLifecyclePage'));
const DispatchSlipsPage = lazy(() => import('./pages/warehouse/DispatchSlipsPage').then(m => ({ default: m.DispatchSlipsPage })));
const DailyDispatchRegisterPage = lazy(() => import('./pages/warehouse/DailyDispatchRegisterPage').then(m => ({ default: m.DailyDispatchRegisterPage })));
const DailyReceivingRegisterPage = lazy(() => import('./pages/warehouse/DailyReceivingRegisterPage').then(m => ({ default: m.DailyReceivingRegisterPage })));
const BarcodeOnboardingPage = lazy(() => import('./pages/warehouse/BarcodeOnboardingPage').then(m => ({ default: m.BarcodeOnboardingPage })));
const CustomerReturnsPage = lazy(() => import('./pages/warehouse/CustomerReturnsPage').then(m => ({ default: m.CustomerReturnsPage })));
const ReturnDashboardPage = lazy(() => import('./pages/warehouse/ReturnDashboardPage').then(m => ({ default: m.ReturnDashboardPage })));
const CashRewardStickersPage = lazy(() => import('./pages/warehouse/CashRewardStickersPage').then(m => ({ default: m.CashRewardStickersPage })));

// ─── Lazy: GST Compliance (3 pages) ───
const EInvoiceDashboard = lazy(() => import('./pages/finance/EInvoiceDashboard').then(m => ({ default: m.EInvoiceDashboard })));
const EWayBillDashboard = lazy(() => import('./pages/finance/EWayBillDashboard').then(m => ({ default: m.EWayBillDashboard })));
const GSTFilingPage = lazy(() => import('./pages/finance/GSTFilingPage').then(m => ({ default: m.GSTFilingPage })));

// ─── Lazy: Logistics (12 pages) ───
const LogisticsRateComparisonPage = lazy(() => import('./pages/logistics/LogisticsRateComparisonPage').then(m => ({ default: m.LogisticsRateComparisonPage })));
// VedaAgroTruckTrackingPage import removed P2 — /veda-agro/truck-tracking now redirects to /logistics/tracker
const TransportPartnersPage = lazy(() => import('./pages/logistics/TransportPartnersPage').then(m => ({ default: m.TransportPartnersPage })));
const ShipmentsPage = lazy(() => import('./pages/logistics/ShipmentsPage').then(m => ({ default: m.ShipmentsPage })));
const ShipmentDetailPage = lazy(() => import('./pages/logistics/ShipmentDetailPage').then(m => ({ default: m.ShipmentDetailPage })));
const LogisticsFlowPage = lazy(() => import('./pages/logistics/LogisticsFlowPage').then(m => ({ default: m.LogisticsFlowPage })));
const DispatchMobileFallbackPage = lazy(() => import('./pages/logistics/DispatchMobileFallbackPage'));
const DispatchConfigPage = lazy(() => import('./pages/logistics/DispatchConfigPage').then(m => ({ default: m.DispatchConfigPage })));
const SODispatchCreatorPage = lazy(() => import('./pages/logistics/SODispatchCreatorPage').then(m => ({ default: m.SODispatchCreatorPage })));
const DispatchSlipDetailPage = lazy(() => import('./pages/logistics/DispatchSlipDetailPage').then(m => ({ default: m.DispatchSlipDetailPage })));
const DeliveryPipelinePage = lazy(() => import('./pages/logistics/DeliveryPipelinePage').then(m => ({ default: m.DeliveryPipelinePage })));
const PostDispatchTrackerPage = lazy(() => import('./pages/logistics/PostDispatchTrackerPage').then(m => ({ default: m.PostDispatchTrackerPage })));
const WaTemplateHealthPage = lazy(() => import('./pages/logistics/WaTemplateHealthPage').then(m => ({ default: m.WaTemplateHealthPage })));
const PaymentGatePage = lazy(() => import('./pages/logistics/PaymentGatePage').then(m => ({ default: m.PaymentGatePage })));

// ─── Lazy: Executive sub-pages (14 — Apr 22, 2026 exec sprint) ───
// Sales exec
const ExecCallQueuePage = lazy(() => import('./pages/executive/sales/CallQueuePage').then(m => ({ default: m.CallQueuePage })));
const ExecMyDealsPage = lazy(() => import('./pages/executive/sales/MyDealsPage').then(m => ({ default: m.MyDealsPage })));
const ExecIncentivesPage = lazy(() => import('./pages/executive/sales/IncentivesPage').then(m => ({ default: m.IncentivesPage })));
const ExecCallHistoryPage = lazy(() => import('./pages/executive/sales/CallHistoryPage').then(m => ({ default: m.CallHistoryPage })));
// Service tech
const ExecMyTicketsPage = lazy(() => import('./pages/executive/service/MyTicketsPage').then(m => ({ default: m.MyTicketsPage })));
const ExecPartsAndExpensesPage = lazy(() => import('./pages/executive/service/PartsAndExpensesPage').then(m => ({ default: m.PartsAndExpensesPage })));
// Production operator
const ExecMyWorkOrdersPage = lazy(() => import('./pages/executive/production/MyWorkOrdersPage').then(m => ({ default: m.MyWorkOrdersPage })));
const ExecMyQCCheckpointsPage = lazy(() => import('./pages/executive/production/MyQCCheckpointsPage').then(m => ({ default: m.MyQCCheckpointsPage })));
// Warehouse exec
const ExecGRNQueuePage = lazy(() => import('./pages/executive/warehouse/GRNQueuePage').then(m => ({ default: m.GRNQueuePage })));
const ExecMyDispatchPage = lazy(() => import('./pages/executive/warehouse/MyDispatchPage').then(m => ({ default: m.MyDispatchPage })));
// Accounts exec
const ExecMatchQueuePage = lazy(() => import('./pages/executive/accounts/MatchQueuePage').then(m => ({ default: m.MatchQueuePage })));
const ExecApprovalsPage = lazy(() => import('./pages/executive/accounts/ApprovalsPage').then(m => ({ default: m.ApprovalsPage })));
// Field partner
const ExecMyClaimsPage = lazy(() => import('./pages/executive/field-partner/MyClaimsPage').then(m => ({ default: m.MyClaimsPage })));
const ExecMyPayoutsPage = lazy(() => import('./pages/executive/field-partner/MyPayoutsPage').then(m => ({ default: m.MyPayoutsPage })));


// Nishtha loan records: staff desk and standalone public document verification.
const LoansDeskPage = lazy(() => import('./pages/loan-records/LoansDeskPage').then(m => ({ default: m.LoansDeskPage })));
const LoanDetailPage = lazy(() => import('./pages/loan-records/LoanDetailPage').then(m => ({ default: m.LoanDetailPage })));
const SanctionQueuePage = lazy(() => import('./pages/loan-records/SanctionQueuePage').then(m => ({ default: m.SanctionQueuePage })));
const LoanSettingsPage = lazy(() => import('./pages/loan-records/LoanSettingsPage').then(m => ({ default: m.LoanSettingsPage })));
const SignerPage = lazy(() => import('./pages/loan-records/SignerPage').then(m => ({ default: m.SignerPage })));
const VerifyDocumentPage = lazy(() => import('./pages/public/VerifyDocumentPage').then(m => ({ default: m.VerifyDocumentPage })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function LoanStaffOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  const roleName = (user?.role?.name ?? '').toLowerCase();
  if (!['admin', 'owner', 'credit_officer'].includes(roleName)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Strict-view route guard — redirects lead_triage_coordinator away from routes
// they have no business reaching (Settings, etc). Server already blocks the
// underlying writes; this just keeps the UI honest.
function StrictViewBlock({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (user?.role?.name === 'lead_triage_coordinator') {
    return <Navigate to="/leads/triage" replace />;
  }
  return <>{children}</>;
}

// Oct 8, 2026 (Developer Access) — Nishtha staff screens are admin/owner only
// (server: requireRole('admin','owner')). Everyone else goes home.
function AdminOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  const roleName = (user?.role?.name ?? '').toLowerCase();
  if (roleName !== 'admin' && roleName !== 'owner') return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Sep 21, 2026 (Track F — Nishtha Grievance Desk). An LSP partner login
// (role.name lsp_manager / lsp_agent) must never land anywhere but its own
// partner subtree — not /dashboard after login, not a stale bookmark to a
// lending/CRM page. Wrapped around the whole protected layout (below) so it
// catches every path, the same way StrictViewBlock catches Settings for
// lead_triage_coordinator, but broader: any path outside /partner/grievances.
function PartnerHomeRedirect({ children }: { children: ReactNode }) {
  const { user } = useAuthStore();
  const location = useLocation();
  const roleName = user?.role?.name;
  const isPartnerLogin = roleName === 'lsp_manager' || roleName === 'lsp_agent';
  // 2026-10-12: + /partner/call-audit (Call Audit partner view).
  if (isPartnerLogin && !location.pathname.startsWith('/partner/grievances') && !location.pathname.startsWith('/partner/call-audit')) {
    return <Navigate to="/partner/grievances" replace />;
  }
  // Oct 8, 2026 (Developer Access) — the LSP developer login lives under
  // /developer only (My credentials, Ask for access, Open once).
  if (roleName === 'lsp_developer' && !location.pathname.startsWith('/developer')) {
    return <Navigate to="/developer/credentials" replace />;
  }
  return <>{children}</>;
}

/**
 * Bug B-2 (May 2026, QA-FIX): standalone /logout route. Previously the only
 * way to log out was via the avatar dropdown in TopBar; navigating to
 * `/logout` returned a 404. Calls the auth store's logout() (which clears
 * the cookie + JWT server-side and resets local state) then redirects
 * to /login. The dropdown handler in TopBar.tsx still works alongside this.
 */
function LogoutPage() {
  const { logout } = useAuthStore();
  const [done, setDone] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await logout();
      } catch {
        // logout() already swallows errors; we still want to redirect.
      } finally {
        if (!cancelled) setDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logout]);

  if (done) return <Navigate to="/login" replace />;
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
    </div>
  );
}

/**
 * Apr 23, 2026 (Bug 1) — reset the ErrorBoundary whenever the URL changes so a
 * crash on one page doesn't stick the user on a fallback after navigation. The
 * keyed wrapper works because React unmounts / remounts children on key change.
 */
function RoutedErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} label={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

// Borrower-portal hosts (blinkemi Track C, 2026-07-03). Existing portals are
// path-based (/portal/client, /portal/vendor) with no hostname switch anywhere
// in the SPA, so this introduces the first host-based default: when the SPA is
// served on a dedicated borrower-portal hostname the WHOLE app is the borrower
// portal — borrowers never see the AURA login. Backend tenant resolution for
// these hosts comes from TenantSetting 'tenants' customDomains (middleware.ts).
const BORROWER_PORTAL_HOSTS = ['portal.blinkemi.com'];

export default function App() {
  const { pathname } = useLocation();
  const isPublicVerification = /^\/verify(?:\/[^/]+)?\/?$/.test(pathname);
  // Host-based portal takeover — borrowers on portal.blinkemi.com get the
  // borrower portal at every path (deep links, refreshes, notification URLs).
  if (BORROWER_PORTAL_HOSTS.includes(window.location.hostname) && !isPublicVerification) {
    return (
      <Routes>
        <Route path="*" element={<BorrowerPortalPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/verify" element={<Suspense fallback={<LazySpinner />}><VerifyDocumentPage /></Suspense>} />
      <Route path="/verify/:sha256" element={<Suspense fallback={<LazySpinner />}><VerifyDocumentPage /></Suspense>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      {/* Bug B-2 (May 2026, QA-FIX): standalone /logout route — clears
          session then redirects to /login. Avatar dropdown in TopBar
          still works in parallel. */}
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/portal/client" element={<ClientPortalPage />} />
      <Route path="/portal/vendor" element={<VendorPortalPage />} />
      {/* Borrower portal (blinkemi) — also reachable path-based on any host */}
      <Route path="/portal/borrower" element={<BorrowerPortalPage />} />
      {/* Public payment confirmation form — customer submits UTR + screenshot */}
      <Route path="/payment-confirmation/:id" element={<PaymentConfirmationForm />} />

      {/* Protected app routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <PartnerHomeRedirect>
            <AppShell>
              <RoutedErrorBoundary>
              <Suspense fallback={<LazySpinner />}>
              <Routes>
                <Route path="/" element={<RoleAwareHome />} />
                <Route path="/dashboard" element={<RoleAwareDashboard />} />
                {/* v6.8.x: /reports was 404 because sidebar's Reports is a group header with no route.
                    Redirect to Accounting → Profit & Loss as the canonical reports surface. */}
                <Route path="/reports" element={<Navigate to="/accounting?tab=pl" replace />} />
                {/* Customers */}
                <Route path="/customers" element={<CustomersPage />} />
                {/* CRM */}
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/contacts/:id" element={<ContactDetailPage />} />
                <Route path="/accounts" element={<AccountsPage />} />
                <Route path="/accounts/:id" element={<AccountDetailPage />} />
                <Route path="/deals" element={<DealsPage />} />
                <Route path="/deals/:id" element={<DealDetailPage />} />
                {/* Sales */}
                <Route path="/proposals" element={<ProposalsPage />} />
                <Route path="/proposals/new" element={<ProposalEditorPage />} />
                <Route path="/proposals/:id/edit" element={<ProposalEditorPage />} />
                <Route path="/discount-requests" element={<DiscountRequestsPage />} />
                {/* Quotes merged into Proposals — redirect estimates URLs */}
                <Route path="/estimates" element={<Navigate to="/proposals" replace />} />
                <Route path="/estimates/new" element={<Navigate to="/proposals/new" replace />} />
                <Route path="/estimates/:id/edit" element={<Navigate to="/proposals" replace />} />
                {/* Sales Orders */}
                <Route path="/sales-orders" element={<SalesOrdersPage />} />
                <Route path="/sales-orders/new" element={<SalesOrderEditorPage />} />
                <Route path="/sales-orders/:id" element={<SalesOrderDetailPage />} />
                <Route path="/sales-orders/:id/edit" element={<SalesOrderEditorPage />} />
                <Route path="/sales-orders/:id/fulfil" element={<SalesOrderFulfilmentPage />} />
                {/* Finance */}
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/invoices/new" element={<InvoiceEditorPage />} />
                <Route path="/invoices/:id" element={<InvoiceEditorPage />} />
                <Route path="/invoices/:id/edit" element={<InvoiceEditorPage />} />
                <Route path="/payments" element={<PaymentsPage />} />
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route path="/credit-notes" element={<CreditNotesPage />} />
                <Route path="/debit-notes" element={<DebitNotesPage />} />
                <Route path="/accounting" element={<LedgerAccountingPage />} />
                {/* NEW-R2-002 fix (Wave-3 May 1 2026): /accounting/ledger was returning
                    SPA 404 because only /accounting was registered. Add alias so that
                    direct navigation to /accounting/ledger lands on the correct page. */}
                <Route path="/accounting/ledger" element={<LedgerAccountingPage />} />
                <Route path="/accounting/legacy" element={<AccountingPage />} />
                {/* Payment Confirmations — Accounts dashboard (auth-gated) */}
                <Route path="/payment-confirmations" element={<PaymentConfirmationPage />} />
                {/* GST Compliance */}
                <Route path="/gst/e-invoices" element={<EInvoiceDashboard />} />
                <Route path="/gst/e-waybills" element={<EWayBillDashboard />} />
                <Route path="/gst/filing" element={<GSTFilingPage />} />
                {/* BUG-26: AccountsExecDashboard navigates to /finance/gst-filing (and
                    /finance/gst-filing?tab=tds) — alias both to /gst/filing to avoid 404.
                    Tab param is preserved via the navigate(); the GSTFilingPage can extend
                    to read ?tab=tds when TDS support is added. */}
                <Route path="/finance/gst-filing" element={<GSTFilingPage />} />
                {/* ── Sprint 2-3 — accounting spine (May 24 2026) ────────────────
                    Purchase Invoices (KK), Ledger pack (LL), Coupons (MM).
                    PaymentSchedulePanel (JJ) is mounted inline in
                    InvoiceEditorPage — no separate route. */}
                <Route path="/purchase-invoices" element={<PurchaseInvoicesPage />} />
                <Route path="/purchase-invoices/new" element={<PurchaseInvoiceEditor />} />
                <Route path="/purchase-invoices/:id" element={<PurchaseInvoiceEditor />} />
                <Route path="/ledger/vouchers" element={<VoucherRegister />} />
                <Route path="/ledger/vouchers/:id" element={<VoucherRegister />} />
                <Route path="/ledger/accounts/:accountId" element={<AccountLedger />} />
                <Route path="/ledger/trial-balance" element={<TrialBalance />} />
                <Route path="/ledger/profit-loss" element={<ProfitLoss />} />
                <Route path="/ledger/balance-sheet" element={<BalanceSheet />} />
                <Route path="/coupons/new" element={<CouponEditor />} />
                <Route path="/coupons/:id/edit" element={<CouponEditor />} />
                {/* Projects */}
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                {/* Warehouse */}
                <Route path="/warehouse/dashboard" element={<InventoryDashboardPage />} />
                <Route path="/warehouse/inward" element={<InwardDashboardPage />} />
                {/* BUG-C08 fix (Wave-3 May 1 2026): /warehouse/grn was returning SPA 404
                    because only /warehouse/inward was registered. GRN (Goods Receipt Note)
                    creation lives in InwardDashboardPage. Add /warehouse/grn as an alias
                    so sidebar "New GRN" links resolve to the correct page. */}
                <Route path="/warehouse/grn" element={<InwardDashboardPage />} />
                <Route path="/warehouse/dispatch-dashboard" element={<DispatchDashboardPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/product-catalogue" element={<ProductCataloguePage />} />
                <Route path="/product-settings" element={<ProductSettingsPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/warehouse/inventory-pipeline" element={<InventoryPipelinePage />} />
                <Route path="/production" element={<ProductionPage />} />
                <Route path="/warehouse/stock-audit" element={<StockAuditPage />} />
                <Route path="/warehouse/barcode-print" element={<BatchBarcodePrintPage />} />
                <Route path="/warehouse/dispatch-register" element={<DailyDispatchRegisterPage />} />
                <Route path="/warehouse/receiving-register" element={<DailyReceivingRegisterPage />} />
                <Route path="/warehouse/barcode-onboarding" element={<BarcodeOnboardingPage />} />
                <Route path="/customer-returns" element={<CustomerReturnsPage />} />
                <Route path="/returns" element={<ReturnDashboardPage />} />
                <Route path="/manufacturing/packing/ss-tank/:id" element={<SsTankPackingPage />} />
                <Route path="/manufacturing/packing/solar-wh/:id" element={<SolarWhPackingPage />} />
                <Route path="/manufacturing/packing/heat-pump/:id" element={<HeatPumpPackingPage />} />
                {/* Warehouse Enhancements */}
                <Route path="/warehouse-locations" element={<WarehouseLocationsPage />} />
                <Route path="/material-boxes" element={<MaterialBoxesPage />} />
                <Route path="/dispatch-slips" element={<DispatchSlipsPage />} />
                {/* Procurement */}
                <Route path="/purchase-dashboard" element={<PurchaseDashboardPage />} />
                <Route path="/vendor-rate-comparison" element={<VendorRateComparisonPage />} />
                <Route path="/vendor-returns" element={<VendorReturnsPage />} />
                <Route path="/vendor-returns/:id" element={<VendorReturnDetailPage />} />
                <Route path="/purchase-requests" element={<PurchaseRequestsPage />} />
                <Route path="/purchase-requests/:id" element={<PurchaseRequestDetailPage />} />
                <Route path="/vendors" element={<VendorsPage />} />
                <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                <Route path="/purchase-orders/new" element={<PurchaseOrderEditorPage />} />
                <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderEditorPage />} />
                {/* ── Generic routes (Phase P2 — Apr 22 2026, tenant-hardcoding cleanup) ── */}
                {/* Samples — B2B sampling workflow, generic */}
                <Route path="/samples" element={<SamplesPage />} />
                {/* Landed cost — any importer needs this */}
                <Route path="/procurement/landed-cost" element={<LandedCostPage />} />
                {/* Invoice swap — commercial ↔ customs invoice for exporters */}
                <Route path="/finance/invoice-swap" element={<InvoiceSwapPage />} />
                {/* Financial approvals — multi-level finance approval queue */}
                <Route path="/finance/approvals" element={<FinancialApprovalsListPage />} />
                {/* Security deposits / EMD tracker */}
                <Route path="/finance/security-deposits" element={<SecurityPaymentPage />} />
                {/* Deal closure checklist */}
                <Route path="/deal-closure" element={<DealClosurePage />} />
                {/* Tally sync (per-tenant integration, configurable) */}
                <Route path="/integrations/tally-sync" element={<TallySyncPage />} />

                {/* ── Legacy /veda-agro/* aliases (GENERALIZE — keep bookmarks working) ── */}
                <Route path="/veda-agro/samples" element={<SamplesPage />} />
                <Route path="/veda-agro/landed-cost" element={<LandedCostPage />} />
                <Route path="/veda-agro/invoice-swap" element={<InvoiceSwapPage />} />
                <Route path="/veda-agro/financial-approvals" element={<FinancialApprovalsListPage />} />
                <Route path="/veda-agro/security-payments" element={<SecurityPaymentPage />} />
                <Route path="/veda-agro/deal-closure" element={<DealClosurePage />} />
                <Route path="/veda-agro/tally-sync" element={<TallySyncPage />} />

                {/* ── Legacy /veda-agro/* redirects (DEPRECATE — duplicate of existing generics) ── */}
                <Route path="/veda-agro/leads" element={<Navigate to="/leads" replace />} />
                <Route path="/veda-agro/deals" element={<Navigate to="/deals" replace />} />
                <Route path="/veda-agro/po-create" element={<Navigate to="/purchase-orders/new" replace />} />
                <Route path="/veda-agro/supplier-quotes" element={<Navigate to="/quotation-jobs" replace />} />
                <Route path="/veda-agro/truck-tracking" element={<Navigate to="/logistics/tracker" replace />} />
                <Route path="/veda-agro/payments" element={<Navigate to="/payments" replace />} />
                <Route path="/veda-agro/advances" element={<Navigate to="/payments?tab=advances" replace />} />
                <Route path="/veda-agro/invoice-upload" element={<Navigate to="/expenses?upload=1" replace />} />
                <Route path="/veda-agro/margin-report" element={<Navigate to="/analytics?view=margin" replace />} />
                {/* Quotation Jobs (Procurement Automation) */}
                <Route path="/quotation-jobs" element={<QuotationJobsPage />} />
                <Route path="/quotation-jobs/:id" element={<QuotationJobDetailPage />} />
                {/* Logistics */}
                <Route path="/transport-partners" element={<TransportPartnersPage />} />
                <Route path="/shipments" element={<ShipmentsPage />} />
                <Route path="/dispatch" element={<DispatchMobileFallbackPage />} />
                <Route path="/shipments/:id" element={<ShipmentDetailPage />} />
                <Route path="/logistics/rate-comparison" element={<LogisticsRateComparisonPage />} />
                <Route path="/logistics/flow" element={<LogisticsFlowPage />} />
                <Route path="/payment-gate" element={<PaymentGatePage />} />
                <Route path="/logistics/pipeline" element={<DeliveryPipelinePage />} />
                <Route path="/logistics/tracker" element={<PostDispatchTrackerPage />} />
                <Route path="/logistics/templates" element={<WaTemplateHealthPage />} />
                <Route path="/dispatch-config" element={<DispatchConfigPage />} />
                <Route path="/dispatch-config/from-so/:salesOrderId" element={<SODispatchCreatorPage />} />
                <Route path="/dispatch-config/slip/:id" element={<DispatchSlipDetailPage />} />
                <Route path="/warehouse/cash-rewards" element={<CashRewardStickersPage />} />
                {/* Field Partners */}
                <Route path="/field-partners" element={<FieldPartnerDirectoryPage />} />
                <Route path="/field-partners/kyc-review" element={<KycReviewPage />} />
                <Route path="/field-partners/claims" element={<InstallationClaimsPage />} />
                <Route path="/field-partners/incentive-rules" element={<IncentiveRulesPage />} />
                <Route path="/field-partners/payouts" element={<PayoutsConsolePage />} />
                <Route path="/field-partners/fraud-review" element={<FraudReviewPage />} />
                <Route path="/field-partners/config" element={<FieldProgramConfigPage />} />
                {/* Procurement Contracts */}
                <Route path="/purchase-contracts" element={<PurchaseContractsPage />} />
                <Route path="/transport-contracts" element={<TransportContractsPage />} />
                {/* Manufacturing */}
                <Route path="/manufacturing/boms" element={<BOMsPage />} />
                <Route path="/boms/new" element={<BOMEditorPage />} />
                <Route path="/boms/:id/edit" element={<BOMEditorPage />} />
                <Route path="/manufacturing/orders/new" element={<ProductionOrderCreatePage />} />
                <Route path="/production-orders/:id" element={<ProductionOrderDetailPage />} />
                <Route path="/manufacturing/orders/:id" element={<ProductionOrderDetailPage />} />
                <Route path="/manufacturing/production-orders/:id" element={<ProductionOrderDetailPage />} />
                <Route path="/qc-templates" element={<QCTemplatesPage />} />
                <Route path="/rejections" element={<RejectionsPage />} />
                <Route path="/manufacturing/dashboard" element={<ProductionDashboardV3 />} />
                {/* Legacy /manufacturing/shop-floor redirects to the v3 dashboard
                    (kanban is now embedded at the bottom). */}
                <Route path="/manufacturing/shop-floor" element={<Navigate to="/manufacturing/dashboard" replace />} />
                <Route path="/manufacturing/planning" element={<ProductionPlanningPage />} />
                <Route path="/production/plan" element={<BucketedProductionPlanPage />} />
                <Route path="/production-planning" element={<BucketedProductionPlanPage />} />
                <Route path="/manufacturing/shift-control" element={<ShiftControlPage />} />
                <Route path="/manufacturing/rm-inspection" element={<RMInspectionPage />} />
                <Route path="/manufacturing/process-kanban" element={<ProcessKanbanPage />} />
                <Route path="/manufacturing/qc" element={<QCManagementPage />} />
                <Route path="/manufacturing/dispatch" element={<ManufacturingDispatchPage />} />
                <Route path="/manufacturing/alerts" element={<ManufacturingAlertsPage />} />
                <Route path="/manufacturing/pipeline" element={<ProductionPipelinePage />} />
                <Route path="/manufacturing/shift-report" element={<EndOfShiftReportPage />} />
                <Route path="/manufacturing/barcodes" element={<BarcodeManagementPage />} />
                <Route path="/manufacturing/scrap" element={<ScrapManagementPage />} />
                <Route path="/manufacturing/pipeline-config" element={<Navigate to="/manufacturing/process-kanban" replace />} />
                <Route path="/manufacturing/fg-activation" element={<FGActivationPage />} />
                <Route path="/manufacturing/floor-inspection" element={<FloorInspectionPage />} />
                <Route path="/warehouse/barcode-lifecycle" element={<BarcodeLifecyclePage />} />
                <Route path="/warehouse/product-units" element={<ProductUnitsPage />} />
                {/* Service Escalation */}
                <Route path="/escalation-rules" element={<EscalationRulesPage />} />
                {/* Lead Integrations */}
                <Route path="/leads" element={<LeadsPage />} />
                {/* Apr 26, 2026 (Squad-B) — Lead Manager workflow */}
                <Route path="/leads/unverified" element={<UnverifiedLeadsPage />} />
                <Route path="/leads/verified" element={<VerifiedLeadsPage />} />
                <Route path="/leads/approvals" element={<LeadApprovalsPage />} />
                {/* Phase 4 (2026-05-26) — Deepika's lead-triage dashboard */}
                <Route path="/leads/triage" element={<LeadTriageDashboard />} />
                <Route path="/leads/dashboard" element={<LeadTriageDashboard />} />
                {/* SO-PO Orchestration */}
                <Route path="/so-po-mapping" element={<SoPoMappingPage />} />
                <Route path="/sales-orders/:soId/margins" element={<MarginCalculatorPage />} />
                <Route path="/sales-orders/:soId/financial-approval" element={<FinancialApprovalPage />} />
                {/* Service */}
                <Route path="/service/sop-dashboard" element={<ServiceSOPDashboardPage />} />
                <Route path="/service/complaints/:id" element={<ComplaintDetailPage />} />
                {/* ITBP SWH Installation Flow */}
                <Route path="/service/itbp-installations" element={<ITBPInstallationsPage />} />
                <Route path="/service/itbp-installations/:id" element={<InstallationJobDetailPage />} />
                <Route path="/service/itbp-reports" element={<ITBPReportsPage />} />
                <Route path="/service-tickets" element={<ServiceTicketsPage />} />
                <Route path="/service-tickets/new" element={<ServiceTicketCreatePage />} />
                <Route path="/service-tickets/:id" element={<ServiceTicketDetailPage />} />
                <Route path="/service-tickets/:id/components" element={<ComponentIssuePage />} />
                <Route path="/service-tickets/:id/photos" element={<ServicePhotosPage />} />
                <Route path="/service-tickets/:id/audit" element={<ServiceAuditPage />} />
                <Route path="/service-tickets/:id/otp" element={<OtpVerifyPage />} />
                <Route path="/inventory/units/:code/history" element={<ProductUnitWarrantyHistoryPage />} />
                <Route path="/support/tickets" element={<SupportTicketsPage />} />
                <Route path="/support/tickets/:id" element={<SupportTicketDetailPage />} />
                <Route path="/products/:id/docs" element={<ProductDocsAdminPage />} />
                <Route path="/service/intake" element={<ComplaintIntakePage />} />
                <Route path="/service/warranty-verify/:id" element={<WarrantyVerificationPage />} />
                <Route path="/service/pre-visit/:id" element={<PreVisitChecklistPage />} />
                <Route path="/service/diagnosis/:id" element={<ProductDiagnosisPage />} />
                <Route path="/service/on-site/:id" element={<OnSiteExecutionPage />} />
                <Route path="/service/warranty-claim/:id" element={<WarrantyClaimPage />} />
                <Route path="/warranties" element={<WarrantiesPage />} />
                {/* HR */}
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/leave-requests" element={<LeavePage />} />
                <Route path="/incentives" element={<IncentivesPage />} />
                {/* Automation */}
                <Route path="/workflows" element={<WorkflowsPage />} />
                {/* Analytics */}
                <Route path="/analytics" element={<AnalyticsPage />} />
                {/* AI */}
                <Route path="/ai" element={<AIAssistantPage />} />
                <Route path="/ai/agents" element={<AIAgentsPage />} />
                <Route path="/ai/integrations" element={<APIIntegrationsPage />} />
                <Route path="/credits" element={<CreditsPage />} />
                {/* Proposal Builder + Knowledge Base */}
                <Route path="/proposal-builder" element={<ProposalBuilderPage />} />
                <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
                {/* Super Admin */}
                <Route path="/admin" element={<SuperAdminDashboard />} />
                <Route path="/admin/tenants" element={<TenantManagementPage />} />
                <Route path="/admin/billing" element={<BillingPage />} />
                <Route path="/admin/health" element={<SystemHealthPage />} />
                <Route path="/admin/team" element={<TeamPage />} />
                <Route path="/admin/audit-logs" element={<AuditLogsPage />} />
                <Route path="/admin/resources" element={<ResourceManagement />} />
                {/* Apr 26, 2026 (Squad-B) — admin lead approval queue */}
                <Route path="/admin/lead-approvals" element={<AdminLeadApprovalsPage />} />
                {/* Apr 30, 2026 (Squad-H) — tenant config admin settings */}
                <Route path="/admin/settings" element={<StrictViewBlock><AdminSettingsPage /></StrictViewBlock>} />
                {/* CR-D (2026-05-01) — carrier virtual-number assignment management */}
                <Route path="/admin/carrier-numbers" element={<CarrierNumbersPage />} />
                {/* Phase BB3b (2026-05-23) — installation focus-box review/edit */}
                <Route path="/admin/installations" element={<InstallationsFocusPage />} />
                {/* Track D (May 2026, Ananya) — Sun Stellar Pool Heat Pump Calculator.
                    Backend gates everything behind `pool_heat_pump_calculator` in
                    tenant.enabledModules + meetingSessionToken — direct URL access
                    falls back to the friendly Launcher screen. */}
                <Route path="/sales/meeting/start" element={<MeetingModeLauncher />} />
                {/* Track D-CHOOSER (May 2026) — intermediate Calculator Chooser
                    page between lead pick and the actual calculator. Lists all
                    calculators enabled for the tenant. */}
                <Route path="/sales/meeting/calculators" element={<CalculatorChooserPage />} />
                <Route path="/sales/meeting/calculator" element={<PoolCalculatorPage />} />
                {/* Track D Calculator #1 (May 2026 reference-port Task 3.6) —
                    Pool Heat Pump calculator (forward + reverse flow). Alias
                    of `/sales/meeting/calculator` so the chooser & launcher
                    links stay deep-linkable. */}
                <Route path="/sales/meeting/calculator/pool" element={<PoolCalculatorPage />} />
                {/* Track D Calculator #2 (May 2026) — Sun Stellar Commercial Heat
                    Pump Calculator. Backend at /v1/commercial-heat-pump/sizing,
                    gated by `commercial_heat_pump_calculator` module flag +
                    meeting session token. */}
                <Route
                  path="/sales/meeting/calculator/commercial-heat-pump"
                  element={<CommercialHeatPumpCalculatorPage />}
                />
                {/* Track D Calculator #3 (May 2026) — Sun Stellar SS Water Tank
                    Calculator. Backend at /v1/ss-tank/sizing, gated by
                    `ss_tank_calculator` module flag + meeting session token.
                    Auto-surfaces in chooser once ST-B registers it in
                    /calculators registry. */}
                <Route
                  path="/sales/meeting/calculator/ss-tank"
                  element={<SsTankCalculatorPage />}
                />
                {/* D7 (June 2026) — Customized Tanks Builder. */}
                <Route
                  path="/sales/meeting/calculator/customized-tank"
                  element={<CustomizedTankCalculatorPage />}
                />
                {/* Track D Calculator #4 (May 2026) — Sun Stellar Domestic
                    Heat Pump (water heating) Calculator. Backend at
                    /v1/domestic-heat-pump/sizing, gated by
                    `domestic_heat_pump_calculator` module flag + meeting
                    session token. Auto-surfaces in chooser once DHP-B
                    registers it in /calculators registry. */}
                <Route
                  path="/sales/meeting/calculator/domestic-heat-pump"
                  element={<DomesticHeatPumpCalculatorPage />}
                />
                <Route path="/admin/pool-models" element={<PoolModelsAdmin />} />
                <Route path="/admin/commercial-heat-pump-models" element={<CommercialHeatPumpModelsAdmin />} />
                <Route path="/admin/ss-tank-models" element={<SsTankModelsAdmin />} />
                <Route path="/admin/domestic-heat-pump-models" element={<DomesticHeatPumpModelsAdmin />} />
                {/* Telecaller */}
                <Route path="/telecaller" element={<TelecallerTodayPage />} />
                <Route path="/telecaller/call/:callId" element={<CallWorkspacePage />} />
                <Route path="/call-logs" element={<CallLogsPage />} />
                {/* ChatKart Call Handoff — agent call history (M4) */}
                <Route path="/calls" element={<MyCallsPage />} />
                <Route path="/offers" element={<ProposalTrackingPage />} />
                <Route path="/team-performance" element={<TeamPerformancePage />} />
                {/* Sales Manager dual-tab pages (Apr 22, 2026 exec sprint — combines /tasks + /team-performance with ?tab=personal|team) */}
                <Route path="/sales/activities" element={<ActivitiesDualPage />} />
                <Route path="/sales/reps" element={<RepsDualPage />} />
                {/* Executive sub-pages (Apr 22, 2026 exec sprint — 14 pages) */}
                <Route path="/executive/sales/call-queue" element={<ExecCallQueuePage />} />
                <Route path="/executive/sales/deals" element={<ExecMyDealsPage />} />
                <Route path="/executive/sales/incentives" element={<ExecIncentivesPage />} />
                <Route path="/executive/sales/history" element={<ExecCallHistoryPage />} />
                <Route path="/executive/service/tickets" element={<ExecMyTicketsPage />} />
                <Route path="/executive/service/parts-expenses" element={<ExecPartsAndExpensesPage />} />
                <Route path="/executive/production/work-orders" element={<ExecMyWorkOrdersPage />} />
                <Route path="/executive/production/qc-checkpoints" element={<ExecMyQCCheckpointsPage />} />
                <Route path="/executive/warehouse/grn" element={<ExecGRNQueuePage />} />
                <Route path="/executive/warehouse/dispatch" element={<ExecMyDispatchPage />} />
                <Route path="/executive/accounts/match-queue" element={<ExecMatchQueuePage />} />
                <Route path="/executive/accounts/approvals" element={<ExecApprovalsPage />} />
                <Route path="/executive/field-partner/claims" element={<ExecMyClaimsPage />} />
                <Route path="/executive/field-partner/payouts" element={<ExecMyPayoutsPage />} />
                {/* Profile */}
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/account" element={<Navigate to="/profile" replace />} />
                {/* Settings — strict-view roles (lead_triage_coordinator) redirected away */}
                {/* AI Assistant rename (Enterprise-only) — must come before wildcard */}
                <Route path="/settings/ai-assistant" element={<StrictViewBlock><AIAssistantSettingsPage /></StrictViewBlock>} />
                <Route path="/settings/*" element={<StrictViewBlock><SettingsPage /></StrictViewBlock>} />
                <Route path="/billing/plans" element={<BillingPlansPage />} />

                {/* ── Lead Manager (Apr 28, 2026 — Squad-D, 12 screens) ── */}
                <Route path="/lead-manager" element={<LeadManagerHome />} />
                <Route path="/lead-manager/unverified" element={<LMUnverifiedQueuePage />} />
                <Route path="/lead-manager/verify/:id" element={<VerifyLeadPage />} />
                <Route path="/lead-manager/verified" element={<LMVerifiedPoolPage />} />
                <Route path="/lead-manager/assigned" element={<AssignedOversightPage />} />
                <Route path="/lead-manager/reassignments" element={<ReassignmentOutboxPage />} />
                <Route path="/lead-manager/metrics" element={<LeadMetricsPage />} />
                <Route path="/lead-manager/search" element={<LeadSearchPage />} />
                <Route path="/lead-manager/leads/:id" element={<LeadDetailPage />} />
                <Route path="/lead-manager/settings" element={<LeadManagerSettingsPage />} />
                {/* /admin/lead-approvals already wired by Squad-B above; the
                    component body has been upgraded to Squad-D's richer card
                    queue, so the existing route now renders the new UI. */}

                {/* ── Lending Ops (Track B, blinkemi launch — Jul 2026) ──
                    Sidebar entries are gated by module 'lending' via
                    tenant.enabledModules; every backend call under
                    /api/v1/lending/* is authenticated + tenant-scoped. */}
                <Route path="/lending" element={<LendingDashboardPage />} />
                <Route path="/lending/applications" element={<LoanApplicationsPage />} />
                <Route path="/lending/loans" element={<LendingLoansPage />} />
                <Route path="/lending/loans/:id" element={<LendingLoanDetailPage />} />
                <Route path="/lending/disbursements" element={<LendingDisbursementsPage />} />
                <Route path="/lending/repayments" element={<LendingRepaymentsPage />} />
                <Route path="/lending/collections" element={<LendingCollectionsPage />} />
                <Route path="/lending/settings" element={<LendingDecisionRulesPage />} />

                {/* ── Nishtha Grievance Desk (Track F, Sep 21 2026) ──
                    Staff screens — sidebar entries module-gated 'lending';
                    server enforces requireRole('credit_officer') (admin/
                    owner/super_admin pass automatically). */}
                <Route path="/grievances" element={<GrievanceDeskPage />} />
                <Route path="/grievances/inbox" element={<GrievanceDeskPage initialFilter="all" />} />
                {/* Sep 22 2026 — approval step: staff review LSP draft replies. */}
                <Route path="/grievances/approvals" element={<GrievanceApprovalsPage />} />
                <Route path="/grievances/cases/:id" element={<GrievanceCasePage />} />
                <Route path="/grievances/partners" element={<GrievancePartnersPage />} />
                <Route path="/grievances/settings" element={<GrievanceSettingsPage />} />
                <Route path="/grievances/how-it-works" element={<GrievanceHowItWorksPage />} />
                {/* Partner (LSP) screens — PartnerHomeRedirect below keeps a
                    partner login inside this subtree everywhere else. */}
                <Route path="/partner/grievances" element={<PartnerCasesPage />} />
                <Route path="/partner/grievances/cases/:id" element={<PartnerCasePage />} />

                {/* ── Nishtha Call Audit (Oct 12 2026) — staff screens: server requireRole('credit_officer');
                    partner view fenced to /api/v1/partner/call-audit. ── */}
                <Route path="/call-audit" element={<CallAuditOverviewPage />} />
                <Route path="/call-audit/calls" element={<CallAuditCallsPage />} />
                <Route path="/call-audit/other-apps" element={<CallAuditOtherAppsPage />} />
                <Route path="/call-audit/reps" element={<CallAuditRepsPage />} />
                <Route path="/call-audit/reps/:rep" element={<CallAuditRepPage />} />
                <Route path="/call-audit/review" element={<CallAuditReviewPage />} />
                <Route path="/partner/call-audit" element={<PartnerCallAuditPage />} />

                {/* ── Nishtha Developer Access + Bank gateway (Oct 8 2026) ──
                    Staff screens, admin/owner only (AdminOnly + server requireRole). */}
                <Route path="/loan-records" element={<LoanStaffOnly><LoansDeskPage /></LoanStaffOnly>} />
                <Route path="/loan-records/:id" element={<LoanStaffOnly><LoanDetailPage /></LoanStaffOnly>} />
                <Route path="/loan-records/queue" element={<LoanStaffOnly><SanctionQueuePage /></LoanStaffOnly>} />
                <Route path="/loan-records/settings" element={<AdminOnly><LoanSettingsPage /></AdminOnly>} />
                <Route path="/loan-records/signer" element={<AdminOnly><SignerPage /></AdminOnly>} />
                <Route path="/dev-access" element={<AdminOnly><DeveloperAccessPage /></AdminOnly>} />
                <Route path="/dev-access/shares" element={<AdminOnly><DeveloperAccessPage view="history" /></AdminOnly>} />
                <Route path="/dev-access/new" element={<AdminOnly><ShareReviewPage /></AdminOnly>} />
                <Route path="/dev-access/shares/:id" element={<AdminOnly><ShareDetailPage /></AdminOnly>} />
                <Route path="/dev-access/gateway" element={<AdminOnly><BankGatewayPage /></AdminOnly>} />
                <Route path="/dev-access/how-it-works" element={<AdminOnly><HowSharingWorksPage /></AdminOnly>} />
                <Route path="/dev-access/videos" element={<AdminOnly><HowToVideosPage audience="staff" /></AdminOnly>} />
                {/* LSP developer screens — PartnerHomeRedirect keeps an lsp_developer login inside /developer. */}
                <Route path="/developer" element={<Navigate to="/developer/credentials" replace />} />
                <Route path="/developer/credentials" element={<MyCredentialsPage />} />
                <Route path="/developer/ask" element={<AskForAccessPage />} />
                <Route path="/developer/credentials/:id/open" element={<OpenOncePage />} />
                <Route path="/developer/videos" element={<DeveloperVideosPage />} />

                {/* ── Route aliases for common mistyped paths (Apr 23, 2026 Agent 4)
                       QA reported blank screens on these — they're not in the
                       sidebar but users (and external doc links) try them. We
                       redirect to the actual registered routes. ── */}
                <Route path="/warehouse" element={<Navigate to="/warehouse/dashboard" replace />} />
                <Route path="/warehouse/inventory" element={<Navigate to="/inventory" replace />} />
                <Route path="/service" element={<Navigate to="/service-tickets" replace />} />
                <Route path="/service/tickets" element={<Navigate to="/service-tickets" replace />} />
                <Route path="/tickets" element={<Navigate to="/service-tickets" replace />} />

                {/* Catch-all 404 — prevents silent blank screens on any future
                    unregistered route. MUST be last. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </RoutedErrorBoundary>
            </AppShell>
            </PartnerHomeRedirect>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
